import React, { AllHTMLAttributes, CSSProperties, FC, PropsWithChildren } from 'react';

import { cn } from 'utils';

import styles from './DataTable.module.scss';

type DataTableProps = AllHTMLAttributes<HTMLDivElement> & {
  columns?: string;
  variant?: 'default' | 'surface';
};

type RowProps = AllHTMLAttributes<HTMLDivElement>;
type CellProps = AllHTMLAttributes<HTMLDivElement> & {
  align?: 'left' | 'center' | 'right';
};

type EmptyProps = {
  children: React.ReactNode;
};

const DataTableRoot: FC<PropsWithChildren<DataTableProps>> = ({
  children,
  columns,
  variant = 'default',
  className,
  style,
  ...props
}) => {
  const cssVars = columns ? ({ '--table-columns': columns } as CSSProperties) : undefined;

  return (
    <div
      className={cn(styles.table, variant === 'surface' && styles.surface, className)}
      style={{ ...cssVars, ...style }}
      {...props}
    >
      {children}
    </div>
  );
};

const Head: FC<PropsWithChildren> = ({ children }) => <div className={styles.head}>{children}</div>;

const HeadCell: FC<PropsWithChildren<CellProps>> = ({
  children,
  align = 'left',
  className,
  ...props
}) => (
  <div className={cn(styles.headCell, styles[`align${align}`], className)} {...props}>
    {children}
  </div>
);

const Body: FC<PropsWithChildren> = ({ children }) => <div className={styles.body}>{children}</div>;

const Row: FC<PropsWithChildren<RowProps>> = ({ children, className, ...props }) => (
  <div className={cn(styles.row, className)} {...props}>
    {children}
  </div>
);

const Cell: FC<PropsWithChildren<CellProps>> = ({
  children,
  align = 'left',
  className,
  ...props
}) => (
  <div className={cn(styles.cell, styles[`align${align}`], className)} {...props}>
    {children}
  </div>
);

const Empty: FC<EmptyProps> = ({ children }) => <div className={styles.empty}>{children}</div>;

type DataTableExtensions = {
  Head: typeof Head;
  HeadCell: typeof HeadCell;
  Body: typeof Body;
  Row: typeof Row;
  Cell: typeof Cell;
  Empty: typeof Empty;
};

const DataTable = DataTableRoot as typeof DataTableRoot & DataTableExtensions;

DataTable.Head = Head;
DataTable.HeadCell = HeadCell;
DataTable.Body = Body;
DataTable.Row = Row;
DataTable.Cell = Cell;
DataTable.Empty = Empty;

export default DataTable;
